import { fetchAuthedApiResponse } from "./http";
import { toApiUrl } from "../config/api";

export const WORK_BASE = "/marketing/api/work";
export function workExportUrl(id: number, format: "json" | "csv") {
  return toApiUrl(`${WORK_BASE}/boards/${id}/export?format=${format}`);
}
export function workFileUrl(path: string | null) {
  return path?.startsWith(`${WORK_BASE}/attachments/`)
    ? toApiUrl(path)
    : undefined;
}
export function subscribeWorkBoard(id: number, onInvalidate: () => unknown) {
  if (typeof EventSource === "undefined") return () => {};
  const events = new EventSource(
    toApiUrl(`${WORK_BASE}/events?board_id=${id}`),
    { withCredentials: true },
  );
  events.onmessage = () => {
    void onInvalidate();
  };
  events.addEventListener("invalidate", () => {
    void onInvalidate();
  });
  events.onerror = () => {
    if (events.readyState === EventSource.CLOSED) void onInvalidate();
  };
  return () => events.close();
}
export interface Member {
  employee_id: number;
  name: string;
  role: string;
  is_active: boolean;
}
export interface WorkSession {
  employee_id: number;
  is_admin: boolean;
  is_gm: boolean;
  workspace: {
    id: number;
    name: string;
    department_values: string[];
    revision: number;
  } | null;
  members: Member[];
  needs_setup: boolean;
  setup_members?: Member[];
  setup_departments?: string[];
}
export interface Label {
  revision: number;
  id: number;
  name: string;
  color: string;
}
export interface CustomField {
  revision: number;
  id: number;
  name: string;
  type: "text" | "number" | "date" | "checkbox" | "dropdown";
  options: string[];
  show_on_card: boolean;
}
export interface Board {
  watching?: boolean;
  id: number;
  workspace_id: number;
  title: string;
  description: string;
  background: string;
  revision: number;
  closed_at: string | null;
  is_template: boolean;
  is_starred: boolean;
  members: Member[];
  settings: {
    members_can_invite: boolean;
    members_can_automate: boolean;
    comment_policy?: "members" | "admins" | "disabled";
  };
  labels: Label[];
  custom_fields: CustomField[];
}
export interface WorkList {
  watching?: boolean;
  id: number;
  board_id: number;
  title: string;
  position: number;
  color: string | null;
  collapsed: boolean;
  archived_at: string | null;
  revision: number;
}
export interface ChecklistItem {
  revision: number;
  id: number;
  title: string;
  position: number;
  completed: boolean;
  employee_id: number | null;
  due_at: string | null;
}
export interface Checklist {
  revision: number;
  id: number;
  title: string;
  position: number;
  items: ChecklistItem[];
}
export interface Card {
  source_unavailable?: boolean;
  source_revision?: number;
  source_board_id?: number;
  source_list_id?: number;
  source_display?: {
    members: Member[];
    labels: Label[];
    custom_fields: CustomField[];
  } | null;
  is_template?: boolean;
  id: number;
  board_id: number;
  list_id: number;
  title: string;
  description: string;
  position: number;
  revision: number;
  member_ids: number[];
  label_ids: number[];
  start_at: string | null;
  due_at: string | null;
  reminder_minutes: number | null;
  completed_at: string | null;
  archived_at: string | null;
  cover: string | null;
  sticker?: "star" | "heart" | "rocket" | "check" | "warning" | null;
  custom_values: Record<string, unknown>;
  location: { lat: number; lng: number; name: string } | null;
  mirror_source_id: number | null;
  checklists: Checklist[];
  comment_count: number;
  attachment_count: number;
  board_title?: string;
  list_title?: string;
}
export interface Comment {
  revision: number;
  my_reactions?: string[];
  id: number;
  employee_id: number;
  name: string;
  body: string;
  created_at: string;
  updated_at: string;
  reactions?: Record<string, number>;
}
export interface Activity {
  id: number;
  employee_id: number;
  name: string;
  kind: string;
  created_at: string;
  data: Record<string, unknown>;
}
export interface Attachment {
  id: number;
  name: string;
  url: string | null;
  content_type: string;
  size: number;
  created_at: string;
  download_url: string | null;
}
export interface CardDetail {
  card: Card;
  comments: Comment[];
  activities: Activity[];
  attachments: Attachment[];
  watching: boolean;
}
export interface Page<T> {
  items: T[];
  total: number;
  next_cursor?: number | null;
  counts?: {
    active: number;
    overdue: number;
    completed: number;
    unassigned: number;
  };
  as_of?: string;
}
export interface BoardPage {
  board: Board;
  lists: WorkList[];
  cards: Card[];
  total: number;
  next_cursor?: number | null;
}
export interface PersonalItem {
  position?: number;
  id: number;
  title: string;
  description?: string;
  revision: number;
  created_at?: string;
  archived_at?: string | null;
}
export interface FocusBlock extends PersonalItem {
  card_id: number | null;
  start_at: string;
  end_at: string;
}
export interface WorkNotification {
  id: number;
  title?: string;
  body?: string;
  kind?: string;
  card_id?: number;
  board_id?: number;
  read_at: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}
export interface Automation {
  created_by: number;
  id: number;
  board_id: number;
  name: string;
  kind: "rule" | "scheduled" | "due" | "card_button" | "board_button";
  enabled: boolean;
  trigger: Record<string, unknown>;
  conditions: Record<string, unknown>[];
  actions: Record<string, unknown>[];
  timezone: string;
  revision: number;
}
export interface AutomationRun {
  id: number;
  status: string;
  error?: string;
  created_at: string;
  data?: Record<string, unknown>;
}
export class WorkApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : JSON.stringify(detail));
    this.status = status;
    this.detail = detail;
  }
}
export function workQuery(
  params: Record<string, string | number | boolean | undefined | null> = {},
) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params))
    if (value !== undefined && value !== null && value !== "")
      search.set(key, String(value));
  return search.size ? `?${search}` : "";
}
let principal: number | null = null;
const pendingCreations = new Map<string, string>();
export function clearWorkRequests() {
  principal = null;
  pendingCreations.clear();
}
function requestSignature(path: string, body: unknown) {
  const value =
    body instanceof FormData
      ? [...body.entries()].map(([name, value]) => [
          name,
          value instanceof File
            ? {
                name: value.name,
                size: value.size,
                type: value.type,
                lastModified: value.lastModified,
              }
            : value,
        ])
      : body;
  return JSON.stringify([principal, path, value]);
}
export async function workRequest<T>(
  path: string,
  method = "GET",
  body?: unknown,
  key?: string,
): Promise<T> {
  const form = body instanceof FormData;
  const signature = method === "POST" ? requestSignature(path, body) : null;
  let idempotencyKey = key;
  if (signature && !idempotencyKey) {
    idempotencyKey = pendingCreations.get(signature) ?? crypto.randomUUID();
    pendingCreations.set(signature, idempotencyKey);
    // Transient retry cache only; this never queues offline writes.
    if (pendingCreations.size > 100)
      pendingCreations.delete(pendingCreations.keys().next().value!);
  }
  const response = await fetchAuthedApiResponse(`${WORK_BASE}${path}`, {
    method,
    credentials: "include",
    headers: {
      ...(!form && body !== undefined
        ? { "Content-Type": "application/json" }
        : {}),
      ...(method === "POST" ? { "Idempotency-Key": idempotencyKey! } : {}),
    },
    body: body === undefined ? undefined : form ? body : JSON.stringify(body),
  });
  if (!response.ok) {
    if (signature && response.status < 500 && response.status !== 410)
      pendingCreations.delete(signature);
    let detail: unknown = response.statusText;
    try {
      detail = (await response.json()).detail ?? detail;
    } catch {
      /* Non-JSON network/proxy errors retain their status. */
    }
    throw new WorkApiError(response.status, detail);
  }
  const result =
    response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  if (signature) pendingCreations.delete(signature);
  return result;
}
export const workApi = {
  session: async () => {
    const session = await workRequest<WorkSession>("/session");
    if (principal !== session.employee_id) pendingCreations.clear();
    principal = session.employee_id;
    return session;
  },
  boards: () => workRequest<Page<Board>>("/boards"),
  board: (id: number, cursor?: number, archived = false) =>
    workRequest<BoardPage>(
      `/boards/${id}${workQuery({ cursor, archived, limit: 100 })}`,
    ),
  card: (id: number) => workRequest<CardDetail>(`/cards/${id}`),
  patchCard: (card: Card, data: Partial<Card>) =>
    workRequest<Card>(`/cards/${card.id}`, "PATCH", {
      revision: card.revision,
      ...(card.mirror_source_id && card.source_revision
        ? { source_revision: card.source_revision }
        : {}),
      ...data,
    }),
  cardAction: (card: Card, action: string, data?: Record<string, unknown>) =>
    workRequest<Card>(`/cards/${card.id}/actions`, "POST", {
      revision: card.revision,
      action,
      data,
    }),
  upload: (id: number, file: File) => {
    const form = new FormData();
    form.set("file", file);
    return workRequest<Attachment>(`/cards/${id}/attachments`, "POST", form);
  },
};
export function workPatch<T = unknown>(
  path: string,
  body: Record<string, unknown>,
) {
  return () => workRequest<T>(path, "PATCH", body);
}
